(function () {
  'use strict';

  /* MeAyurv — Cart Milestone / Free Gift (canonical, consistent with the
     SVG-icon snippet + node-positioned CSS).
     - Fill is set on the persistent cart-drawer element (no flicker).
     - Fill uses node positions that MUST match the CSS .step:nth-child left:%.
     - JS NEVER writes to the badge content — icons are server-rendered SVGs,
       coloured purely by the .is-unlocked class. (Touching dot.textContent
       wipes the SVG, which caused the "icons vanish after 1s" bug.)
     - Drawer-only. Gift pixel suppression is done in Shopify Customer Events. */

  const CONFIG = {
    currencySymbol: '₹',
    gifts: [
      { variantId: '47539517620402', threshold: 89900, quantity: 1 },
      { variantId: '47539519455410', threshold: 169900, quantity: 1 }
    ],
    // unlockedLabel = shown in "You unlocked X" (free shipping always listed,
    // since every tier includes it). nextLabel = shown in "Add ₹x to unlock Y"
    // (free shipping only on tier 1, because once any tier is reached free
    // shipping is already unlocked and needn't be repeated).
    // These strings MUST stay identical to the Liquid snippet's current_reward /
    // next_reward values, or the first cart interaction causes a text flicker.
    // percent = the % order discount for that tier (used to show estimated
    // savings in the celebration). word = celebratory button label per milestone.
    tiers: [
      { threshold: 29900,  valueText: '₹299',  percent: 5,  word: 'Awesome',     unlockedLabel: '5% OFF + Free Shipping',                 nextLabel: '5% OFF + Free Shipping' },
      { threshold: 49900,  valueText: '₹499',  percent: 10, word: 'Brilliant!',  unlockedLabel: '10% OFF + Free Shipping',                nextLabel: '10% OFF + Free Shipping' },
      { threshold: 89900,  valueText: '₹899',  percent: 15, word: 'WooHoo!',     unlockedLabel: '15% OFF + Free Shipping + 1 Free Gift',  nextLabel: '15% OFF + 1 Free Gift' },
      { threshold: 169900, valueText: '₹1699', percent: 20, word: 'Incredible!', unlockedLabel: '20% OFF + Free Shipping + 2 Free Gifts', nextLabel: '20% OFF + 2 Free Gifts' }
    ]
  };

  const GATE_FAILSAFE_MS = 8000;
  const GATE_IDLE_QUIET_MS = 400;

  const giftVariantIds = CONFIG.gifts.map((g) => String(g.variantId));

  // Visual position (% along the track) of each milestone badge, so the fill
  // lands exactly on each badge. These are derived from the badge centres
  // (measured at wrap%: 16.3 / 40.4 / 64.6 / 88.7) mapped onto the track, which
  // runs left:5% -> right-edge:88.7% (see .meayurv-cart-rewards__track in CSS).
  // node = (badgeWrap% - 5) / 83.7 * 100. The last node is 100 so the fill ends
  // exactly at the last badge. If you change the track left/right insets or the
  // .meayurv-cart-rewards__steps padding, re-derive these.
  const MEAYURV_NODE_POS = [13.5, 42.3, 71.2, 100];

  function meayurvFillPercent(value) {
    const t = CONFIG.tiers.map((x) => x.threshold);
    if (value <= 0) return 0;
    if (value >= t[t.length - 1]) return 100;
    if (value < t[0]) return MEAYURV_NODE_POS[0] * (value / t[0]);
    for (let i = 1; i < t.length; i++) {
      if (value < t[i]) {
        const frac = (value - t[i - 1]) / (t[i] - t[i - 1]);
        return MEAYURV_NODE_POS[i - 1] + (MEAYURV_NODE_POS[i] - MEAYURV_NODE_POS[i - 1]) * frac;
      }
    }
    return MEAYURV_NODE_POS[MEAYURV_NODE_POS.length - 1];
  }

  /* ---------- Checkout gate (single source of truth) ---------- */
  const CheckoutGate = (() => {
    const SELECTOR = 'button[name="checkout"]';
    const BUSY_LABEL = 'Finalizing cart rewards...';
    let current = false;

    function buttons() { return document.querySelectorAll(SELECTOR); }

    function paint() {
      buttons().forEach((button) => {
        if (current) {
          if (!button.dataset.gateLabel) button.dataset.gateLabel = button.textContent.trim() || 'Checkout';
          button.disabled = true;
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.textContent = BUSY_LABEL;
        } else {
          button.disabled = false;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          if (button.dataset.gateLabel) { button.textContent = button.dataset.gateLabel; delete button.dataset.gateLabel; }
        }
      });
    }

    return {
      set(busy) { current = !!busy; paint(); },
      reassert() { paint(); },
      isBusy() { return current; }
    };
  })();

  /* ---------- Helpers ---------- */
  function getRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  }
  function getCartSectionUrl() {
    const base = (window.routes && window.routes.cart_url) ? window.routes.cart_url : getRoot() + 'cart';
    return base + '?section_id=cart-drawer';
  }
  function formatMoney(paise) {
    const rupees = Math.max(0, Number(paise) || 0) / 100;
    return CONFIG.currencySymbol + new Intl.NumberFormat('en-IN', { maximumFractionDigits: rupees % 1 === 0 ? 0 : 2 }).format(rupees);
  }
  function isGiftVariant(variantId) { return giftVariantIds.includes(String(variantId)); }
  function getRewardSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((total, item) => isGiftVariant(item.variant_id) ? total : total + (Number(item.original_line_price) || 0), 0);
  }
  function getPaidItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((count, item) => isGiftVariant(item.variant_id) ? count : count + (Number(item.quantity) || 0), 0);
  }
  function getTierState(cartValue) {
    let currentIndex = -1, currentTier = null, nextTier = CONFIG.tiers[0];
    CONFIG.tiers.forEach((tier, index) => {
      if (cartValue >= tier.threshold) { currentIndex = index; currentTier = tier; nextTier = CONFIG.tiers[index + 1] || null; }
    });
    return { currentIndex, currentTier, nextTier, isComplete: currentIndex === CONFIG.tiers.length - 1 };
  }

  /* ---------- Milestone celebration (confetti + slide-up savings sheet) ----------
     Fires ONLY when the cart crosses UP into a not-yet-celebrated milestone, once
     per milestone. The overlay is appended to the PERSISTENT cart-drawer element,
     so a Sense re-render of .drawer__inner can't kill it mid-animation. It is
     pointer-events:none except the sheet, auto-closes in <=3s, and NEVER disables
     or owns the checkout button (independent of CheckoutGate + Magic Checkout).
     No third-party library — a tiny self-contained canvas confetti. */
  const Celebration = (() => {
    const SHEET_MS = 3000;    // panel auto-close (max 3s) — also the button-fill time
    const CONFETTI_MS = 2800; // confetti run time
    let overlay, canvas, rewardEl, savingsEl, fillEl, labelEl, hideTimer, stopConfetti, fillAnim;

    function reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function ensureDOM(host) {
      if (overlay && host.contains(overlay)) return;
      overlay = document.createElement('div');
      overlay.className = 'meayurv-celebrate';
      overlay.innerHTML =
        '<canvas class="meayurv-celebrate__canvas"></canvas>' +
        '<div class="meayurv-celebrate__sheet" role="status" aria-live="polite">' +
          '<div class="meayurv-celebrate__title">Milestone unlocked!</div>' +
          '<div class="meayurv-celebrate__reward"></div>' +
          '<div class="meayurv-celebrate__savings"></div>' +
          '<button type="button" class="meayurv-celebrate__btn">' +
            '<span class="meayurv-celebrate__btn-fill" aria-hidden="true"></span>' +
            '<span class="meayurv-celebrate__btn-label"></span>' +
          '</button>' +
        '</div>';
      host.appendChild(overlay);
      canvas = overlay.querySelector('.meayurv-celebrate__canvas');
      rewardEl = overlay.querySelector('.meayurv-celebrate__reward');
      savingsEl = overlay.querySelector('.meayurv-celebrate__savings');
      fillEl = overlay.querySelector('.meayurv-celebrate__btn-fill');
      labelEl = overlay.querySelector('.meayurv-celebrate__btn-label');
      overlay.querySelector('.meayurv-celebrate__btn').addEventListener('click', hide);
    }

    function runConfetti() {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const W = canvas.clientWidth, H = canvas.clientHeight;
      if (!W || !H) return function () {};
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const colors = ['#d65a31', '#c2410c', '#f4a261', '#2a9d8f', '#e9c46a', '#e76f51', '#ffd166'];
      const N = Math.min(150, Math.max(70, Math.round(W * 0.5)));
      const parts = [];
      for (let i = 0; i < N; i++) {
        parts.push({
          x: Math.random() * W,
          y: -20 - Math.random() * H * 0.6,
          w: 5 + Math.random() * 6,
          h: 7 + Math.random() * 8,
          color: colors[i % colors.length],
          vx: -1 + Math.random() * 2,
          vy: 2 + Math.random() * 3,
          rot: Math.random() * Math.PI,
          vr: -0.2 + Math.random() * 0.4
        });
      }
      let startTs = null, raf = 0;
      function frame(ts) {
        if (startTs === null) startTs = ts;
        const elapsed = ts - startTs;
        const fade = elapsed > CONFETTI_MS - 600 ? Math.max(0, (CONFETTI_MS - elapsed) / 600) : 1;
        ctx.clearRect(0, 0, W, H);
        let alive = false;
        for (const p of parts) {
          p.x += p.vx; p.y += p.vy; p.vy += 0.03; p.rot += p.vr;
          if (p.y < H + 20) alive = true;
          ctx.save();
          ctx.globalAlpha = fade;
          ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        }
        if (elapsed < CONFETTI_MS && alive) raf = window.requestAnimationFrame(frame);
        else ctx.clearRect(0, 0, W, H);
      }
      raf = window.requestAnimationFrame(frame);
      return function () { window.cancelAnimationFrame(raf); ctx.clearRect(0, 0, W, H); };
    }

    function show(tierIndex, subtotal) {
      const host = document.querySelector('cart-drawer');
      const tier = CONFIG.tiers[tierIndex];
      if (!host || !tier) return;
      ensureDOM(host);
      const saving = Math.round((tier.percent / 100) * subtotal);
      rewardEl.textContent = tier.unlockedLabel;
      savingsEl.innerHTML = "You're saving <strong>" + formatMoney(saving) + '</strong>';
      labelEl.textContent = tier.word;

      window.clearTimeout(hideTimer);
      if (stopConfetti) { stopConfetti(); stopConfetti = null; }
      if (fillAnim) { try { fillAnim.cancel(); } catch (e) {} fillAnim = null; }

      overlay.classList.add('is-active');

      if (reducedMotion()) {
        fillEl.style.transform = 'scaleX(0)';
        hideTimer = window.setTimeout(hide, 2600);
        return;
      }

      window.requestAnimationFrame(() => { stopConfetti = runConfetti(); });

      // The button starts faded (cream overlay covering it) and "fills dark" as
      // the overlay retreats over 3s. When it finishes, the panel closes — so the
      // fill IS the auto-close countdown.
      fillEl.style.transform = 'scaleX(1)';
      fillAnim = fillEl.animate(
        [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }],
        { duration: SHEET_MS, easing: 'linear', fill: 'forwards' }
      );
      fillAnim.onfinish = hide;
      hideTimer = window.setTimeout(hide, SHEET_MS + 250); // failsafe if onfinish is throttled
    }

    function hide() {
      if (!overlay) return;
      window.clearTimeout(hideTimer);
      if (fillAnim) { try { fillAnim.cancel(); } catch (e) {} fillAnim = null; }
      overlay.classList.remove('is-active');
    }

    // The drawer may not be open yet (e.g. add-to-cart from a product page opens
    // it a moment later). Wait briefly for it to become active, then celebrate.
    function fire(tierIndex, subtotal) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      if (drawer.classList.contains('active')) { show(tierIndex, subtotal); return; }
      let tries = 0;
      const iv = window.setInterval(() => {
        if (drawer.classList.contains('active')) { window.clearInterval(iv); show(tierIndex, subtotal); }
        else if (++tries > 30) window.clearInterval(iv); // ~1.5s, give up quietly
      }, 50);
    }

    return { fire };
  })();

  let meayurvLastCelebratedTier = -1;
  let meayurvCelebrationPrimed = false;
  function maybeCelebrate(currentIndex, subtotal) {
    if (!meayurvCelebrationPrimed) {
      // First render after load: remember the starting tier, don't celebrate a
      // cart that already qualified before this session.
      meayurvLastCelebratedTier = currentIndex;
      meayurvCelebrationPrimed = true;
      return;
    }
    if (currentIndex > meayurvLastCelebratedTier) {
      meayurvLastCelebratedTier = currentIndex;
      Celebration.fire(currentIndex, subtotal);
    } else if (currentIndex < meayurvLastCelebratedTier) {
      meayurvLastCelebratedTier = currentIndex; // re-arm so re-crossing celebrates again
    }
  }

  /* ---------- Remove-item confirmation (slide-up sheet) ----------
     Intercepts a click on a cart item's trash button and asks for confirmation
     in a bottom sheet first (Organic India style). Gifts are never confirmed
     here (their control is hidden + the gift-row guard blocks them). The button
     reads "Yes, remove" normally, or "Remove and go back to store" when it's the
     last NON-GIFT line (removing it empties the cart) — then we close the drawer.
     The actual removal is performed by re-firing the real button click, so Sense's
     own cart mechanism + our reconcile still run exactly as before. */
  const RemoveConfirm = (() => {
    let overlay, imgEl, nameEl, metaEl, priceNowEl, priceWasEl, confirmBtn;
    let pendingBtn = null, isLast = false, bypass = false;

    function nonGiftLineCount() {
      let n = 0;
      document.querySelectorAll('cart-drawer [data-meayurv-cart-line]').forEach((r) => {
        if (r.dataset.meayurvIsGift !== 'true') n++;
      });
      return n;
    }

    function text(el) { return el ? el.textContent.trim() : ''; }

    function ensureDOM(host) {
      if (overlay && host.contains(overlay)) return;
      overlay = document.createElement('div');
      overlay.className = 'meayurv-removeconfirm';
      overlay.innerHTML =
        '<div class="meayurv-removeconfirm__backdrop"></div>' +
        '<div class="meayurv-removeconfirm__sheet" role="dialog" aria-modal="true" aria-label="Remove item">' +
          '<div class="meayurv-removeconfirm__head">' +
            '<span class="meayurv-removeconfirm__title">Do you want to remove 1 item?</span>' +
            '<button type="button" class="meayurv-removeconfirm__close" aria-label="Cancel">&times;</button>' +
          '</div>' +
          '<div class="meayurv-removeconfirm__item">' +
            '<img class="meayurv-removeconfirm__img" alt="" />' +
            '<div class="meayurv-removeconfirm__info">' +
              '<div class="meayurv-removeconfirm__name"></div>' +
              '<div class="meayurv-removeconfirm__meta"></div>' +
            '</div>' +
            '<div class="meayurv-removeconfirm__price">' +
              '<span class="meayurv-removeconfirm__price-now"></span>' +
              '<s class="meayurv-removeconfirm__price-was"></s>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="meayurv-removeconfirm__confirm"></button>' +
        '</div>';
      host.appendChild(overlay);
      imgEl = overlay.querySelector('.meayurv-removeconfirm__img');
      nameEl = overlay.querySelector('.meayurv-removeconfirm__name');
      metaEl = overlay.querySelector('.meayurv-removeconfirm__meta');
      priceNowEl = overlay.querySelector('.meayurv-removeconfirm__price-now');
      priceWasEl = overlay.querySelector('.meayurv-removeconfirm__price-was');
      confirmBtn = overlay.querySelector('.meayurv-removeconfirm__confirm');
      overlay.querySelector('.meayurv-removeconfirm__close').addEventListener('click', cancel);
      overlay.querySelector('.meayurv-removeconfirm__backdrop').addEventListener('click', cancel);
      confirmBtn.addEventListener('click', confirmRemove);
    }

    function open(removeBtn) {
      const host = document.querySelector('cart-drawer');
      if (!host) return;
      const row = removeBtn.closest('[data-meayurv-cart-line]') || removeBtn.closest('.cart-item');
      if (!row || row.dataset.meayurvIsGift === 'true') return;
      ensureDOM(host);
      pendingBtn = removeBtn;

      const img = row.querySelector('.cart-item__image');
      if (img && img.src) { imgEl.src = img.src; imgEl.style.display = ''; }
      else { imgEl.removeAttribute('src'); imgEl.style.display = 'none'; }

      nameEl.textContent = text(row.querySelector('.cart-item__name')) || 'this item';

      const dds = Array.from(row.querySelectorAll('.cart-item__details dl dd')).map((d) => d.textContent.trim()).filter(Boolean);
      metaEl.textContent = dds.join(' · ');
      metaEl.style.display = dds.length ? '' : 'none';

      priceNowEl.textContent = text(row.querySelector('.cart-item__totals .price'));
      const was = text(row.querySelector('.cart-item__totals .cart-item__old-price'));
      priceWasEl.textContent = was;
      priceWasEl.style.display = was ? '' : 'none';

      isLast = nonGiftLineCount() <= 1;
      confirmBtn.textContent = isLast ? 'Remove and go back to store' : 'Yes, remove';
      overlay.classList.add('is-active');
    }

    async function confirmRemove() {
      const btn = pendingBtn;
      const last = isLast;
      close();
      if (!btn) return;

      if (last) {
        // Last paid item -> "Remove and go back to store". Clear the WHOLE cart
        // (awaited, so it's committed before we leave) — this also drops any ₹0
        // gift lines, avoiding a stranded gift-only cart — then go to All Products.
        try {
          await fetch(getRoot() + 'cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
          });
        } catch (e) { /* navigate anyway — clearing is best-effort */ }
        window.location.href = getRoot() + 'collections/all';
        return;
      }

      // Otherwise re-fire the real remove click, bypassing our own interceptor.
      bypass = true;
      try { btn.click(); } finally { bypass = false; }
    }

    function cancel() { close(); }
    function close() { pendingBtn = null; if (overlay) overlay.classList.remove('is-active'); }
    function bypassing() { return bypass; }

    return { open, bypassing };
  })();

  /* ---------- In-cart upsell (two tabs: "Complete Your Ritual" + Bestsellers) ----------
     Tab 1 ("ritual") is filled here from Shopify's Recommendations API with
     intent=complementary (configured per product in the Search & Discovery app),
     aggregated across the cart's products, de-duped, excluding items already in the
     cart and the gifts, capped at 10. Tab 2 (bestsellers) is server-rendered in the
     Liquid snippet from the 'our-bestsellers' collection. Cards prefer the 100g
     variant. The track scrolls horizontally (swipe + arrows) while vertical scrolls
     chain through to the cart (overflow-y:hidden in CSS). Re-injects after re-renders;
     re-fetches only when the set of cart products changes. */
  const Upsell = (() => {
    let lastKey = '';
    let cachedHTML = '';
    let inFlight = false;
    // UI state that must survive a cart re-render (Sense's cart.js replaces the
    // whole cart-drawer-items on every qty change, rebuilding the upsell DOM):
    let activeTab = 'ritual';                        // which tab the user is on
    const scrollPos = { ritual: 0, bestsellers: 0 }; // last scrollLeft per tab

    function injectRitual(html) {
      document.querySelectorAll('[data-meayurv-upsell-track="ritual"]').forEach((track) => {
        if (html) { track.innerHTML = html; }
        else {
          const msg = track.getAttribute('data-meayurv-upsell-empty') || '';
          track.innerHTML = msg ? '<p class="meayurv-upsell__empty">' + msg + '</p>' : '';
        }
      });
    }

    function updateTrackArrows(track) {
      const panel = track.closest('[data-meayurv-upsell-panel]');
      if (!panel) return;
      const prev = panel.querySelector('.meayurv-upsell__arrow--prev');
      const next = panel.querySelector('.meayurv-upsell__arrow--next');
      if (!prev || !next) return;
      const overflow = (track.scrollWidth - track.clientWidth) > 4;
      if (!overflow) { prev.hidden = true; next.hidden = true; return; }
      // Hide the left arrow at the start and the right arrow at the end.
      prev.hidden = track.scrollLeft <= 2;
      next.hidden = (track.scrollLeft + track.clientWidth) >= (track.scrollWidth - 2);
    }

    function setupArrows() {
      document.querySelectorAll('.meayurv-upsell__track').forEach(updateTrackArrows);
    }

    // Re-apply the user's active tab + scroll position after a cart re-render
    // rebuilds the upsell DOM. Without this, every +/- snaps back to the default
    // "ritual" tab at scrollLeft 0. Set the tab classes FIRST (so the right panel
    // is visible), then restore scrollLeft on the now-visible track.
    function applyState() {
      document.querySelectorAll('[data-meayurv-upsell]').forEach((block) => {
        block.querySelectorAll('[data-meayurv-upsell-tab]').forEach((t) =>
          t.classList.toggle('is-active', t.getAttribute('data-meayurv-upsell-tab') === activeTab));
        block.querySelectorAll('[data-meayurv-upsell-panel]').forEach((p) =>
          p.classList.toggle('is-active', p.getAttribute('data-meayurv-upsell-panel') === activeTab));
      });
      document.querySelectorAll('.meayurv-upsell__track').forEach((track) => {
        const tab = track.getAttribute('data-meayurv-upsell-track');
        if (tab && scrollPos[tab] != null) track.scrollLeft = scrollPos[tab];
      });
    }

    function afterRender() { applyState(); setupArrows(); }

    async function sync(cart) {
      if (!document.querySelector('[data-meayurv-upsell]')) return;
      const items = (cart && cart.items) || [];
      const giftSet = new Set(giftVariantIds.map(String));
      const cartProductIds = [];
      items.forEach((it) => { if (!giftSet.has(String(it.variant_id))) cartProductIds.push(String(it.product_id)); });
      const uniqueIds = [...new Set(cartProductIds)];
      const key = uniqueIds.slice().sort().join(',');

      if (!uniqueIds.length) { lastKey = ''; cachedHTML = ''; injectRitual(''); afterRender(); return; }
      if (key === lastKey) { injectRitual(cachedHTML); afterRender(); return; }
      if (inFlight) return;

      inFlight = true;
      try {
        const seen = new Set(uniqueIds);
        // Curated complementary products first (set per-product in Search &
        // Discovery). If NONE are configured for any cart product, fall back to
        // Shopify's algorithmic "related" products so the tab is never empty.
        // When complementary exist, they win and the fallback never runs.
        let merged = await fetchRecs(uniqueIds, 'complementary', seen);
        if (!merged.length) merged = await fetchRecs(uniqueIds, 'related', seen);
        cachedHTML = merged.slice(0, 10).join('');
        lastKey = key;
        injectRitual(cachedHTML);
        afterRender();
      } finally {
        inFlight = false;
      }
    }

    // Aggregate recommendation CARDS (rendered HTML, so they carry the short-title
    // metafield) across the cart's products (first 4), de-duped by product id and
    // excluding anything already in `seen` (cart items + already-picked). The
    // section itself skips Reward Gifts. Returns an array of card HTML strings.
    async function fetchRecs(ids, intent, seen) {
      const out = [];
      for (const pid of ids.slice(0, 4)) {
        try {
          const r = await fetch(getRoot() + 'recommendations/products?section_id=meayurv-upsell-reco&product_id=' + pid + '&intent=' + intent + '&limit=10', { headers: { Accept: 'text/html' } });
          if (!r.ok) continue;
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          doc.querySelectorAll('.meayurv-upsell__card').forEach((card) => {
            const cpid = card.getAttribute('data-product-id');
            if (cpid && !seen.has(String(cpid))) { seen.add(String(cpid)); out.push(card.outerHTML); }
          });
        } catch (e) { /* skip this product's recs */ }
        if (out.length >= 10) break;
      }
      return out;
    }

    function reinject() { injectRitual(cachedHTML); afterRender(); }

    async function updateCartCount() {
      try {
        const r = await fetch(getRoot() + '?section_id=cart-icon-bubble');
        if (!r.ok) return;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const fresh = doc.querySelector('#cart-icon-bubble');
        const cur = document.querySelector('#cart-icon-bubble');
        if (fresh && cur) cur.innerHTML = fresh.innerHTML;
      } catch (e) { /* non-critical */ }
    }

    async function addToCart(variantId, btn) {
      if (!variantId) return;
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        const r = await fetch(getRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!r.ok) throw new Error('add failed');
        await renderDrawerAfterGiftChange();   // show the new line + footer
        const cart = await fetchCart();
        renderFromCart(cart);                  // rewards + celebration + upsell re-sync
        runReconcile();                        // add a gift if this crossed a tier
        updateCartCount();                     // header cart count
      } catch (e) {
        console.warn('[MeAyurv] upsell add failed:', e);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    function bind() {
      if (window.__meayurvUpsellBound) return;
      window.__meayurvUpsellBound = true;
      document.addEventListener('click', (e) => {
        const tabBtn = e.target.closest('[data-meayurv-upsell-tab]');
        if (tabBtn) {
          const block = tabBtn.closest('[data-meayurv-upsell]');
          if (!block) return;
          const name = tabBtn.getAttribute('data-meayurv-upsell-tab');
          activeTab = name; // remember across cart re-renders
          block.querySelectorAll('[data-meayurv-upsell-tab]').forEach((t) => t.classList.toggle('is-active', t === tabBtn));
          block.querySelectorAll('[data-meayurv-upsell-panel]').forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-meayurv-upsell-panel') === name));
          const newTrack = block.querySelector('[data-meayurv-upsell-track="' + name + '"]');
          if (newTrack && scrollPos[name] != null) newTrack.scrollLeft = scrollPos[name];
          setupArrows();
          return;
        }
        const arrow = e.target.closest('.meayurv-upsell__arrow');
        if (arrow) {
          const panel = arrow.closest('[data-meayurv-upsell-panel]');
          const track = panel && panel.querySelector('.meayurv-upsell__track');
          if (track) {
            const dir = arrow.classList.contains('meayurv-upsell__arrow--next') ? 1 : -1;
            track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
          }
          return;
        }
        const addBtn = e.target.closest('[data-meayurv-upsell-add]');
        if (addBtn) {
          e.preventDefault();
          addToCart(addBtn.getAttribute('data-variant-id'), addBtn);
        }
      });
      // Update arrow visibility as a track scrolls (scroll doesn't bubble, so
      // listen in the capture phase). Smooth arrow scrolls also fire this.
      document.addEventListener('scroll', (e) => {
        const t = e.target;
        if (t && t.classList && t.classList.contains('meayurv-upsell__track')) {
          const tab = t.getAttribute('data-meayurv-upsell-track');
          if (tab) scrollPos[tab] = t.scrollLeft; // remember scroll across re-renders
          updateTrackArrows(t);
        }
      }, true);
      window.addEventListener('resize', setupArrows);
    }

    return { sync, reinject, setupArrows, bind };
  })();

  /* ---------- Savings breakdown panel (expand/collapse) ----------
     The panel markup + all its numbers are server-rendered in cart-drawer.liquid
     and refresh on every cart re-render. This module only owns the open/closed
     state: it toggles on click and RE-APPLIES the state after a re-render (Sense
     replaces .cart-drawer__footer on qty change, which would otherwise snap the
     panel shut — same persistence pattern as the Upsell tabs). */
  const SavingsPanel = (() => {
    let open = false;
    function closeNote() {
      const note = document.querySelector('#Details-CartDrawer');
      if (note) note.removeAttribute('open');
    }
    function apply() {
      document.querySelectorAll('[data-meayurv-savings]').forEach((el) => {
        el.classList.toggle('is-open', open);
        const btn = el.querySelector('[data-meayurv-savings-toggle]');
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    function bind() {
      if (window.__meayurvSavingsBound) return;
      window.__meayurvSavingsBound = true;
      document.addEventListener('click', (e) => {
        // Toggle the savings panel; opening it closes the order-note (mutual exclusivity).
        if (e.target.closest('[data-meayurv-savings-toggle]')) {
          open = !open;
          if (open) closeNote();
          apply();
          return;
        }
        // Opening the order-note closes the savings panel (vice versa). The note is
        // a native <details>; after its own toggle settles, sync our state.
        if (e.target.closest('#Details-CartDrawer summary')) {
          window.setTimeout(() => {
            const note = document.querySelector('#Details-CartDrawer');
            if (note && note.open && open) { open = false; apply(); }
          }, 0);
        }
      });
    }
    return { bind, apply };
  })();

  /* ---------- Rendering ---------- */
  function renderRewards(cartValue, itemCount) {
    const rewardBlocks = document.querySelectorAll('[data-meayurv-cart-rewards]');
    if (!rewardBlocks.length) return;

    const state = getTierState(cartValue);
    const progressPercent = meayurvFillPercent(cartValue);

    // Fill lives on the PERSISTENT cart-drawer element so a Sense re-render of
    // .drawer__inner (which rebuilds the bar) can't reset it -> no flicker.
    const fillHost = document.querySelector('cart-drawer') || document.documentElement;
    fillHost.style.setProperty('--meayurv-reward-progress', progressPercent + '%');

    rewardBlocks.forEach((block) => {
      block.dataset.cartValue = String(cartValue);
      block.dataset.currentTier = String(state.currentIndex + 1);

      const banner = block.querySelector('.meayurv-cart-rewards__banner');
      const message = block.querySelector('.meayurv-cart-rewards__message');
      const steps = block.querySelectorAll('.meayurv-cart-rewards__step');

      if (banner) {
        banner.classList.toggle('is-complete', state.isComplete);
        if (!itemCount) banner.textContent = 'Add items to unlock discounts, free shipping & gifts';
        else if (state.isComplete) banner.textContent = "Yay! You've unlocked the best offer";
        else if (state.currentTier) banner.textContent = "Yay! You've unlocked extra savings";
        else banner.textContent = 'Unlock discounts, free shipping & gifts';
      }

      if (message) {
        if (!itemCount) {
          message.innerHTML = 'Add <strong>' + formatMoney(CONFIG.tiers[0].threshold) + '</strong> to unlock <strong>' + CONFIG.tiers[0].nextLabel + '</strong>.';
        } else if (state.isComplete) {
          message.innerHTML = 'You unlocked <strong>' + state.currentTier.unlockedLabel + '</strong>.';
        } else if (state.currentTier && state.nextTier) {
          const remaining = state.nextTier.threshold - cartValue;
          message.innerHTML = 'You unlocked <strong>' + state.currentTier.unlockedLabel + '</strong>. Add <strong>' + formatMoney(remaining) + '</strong> more to unlock <strong>' + state.nextTier.nextLabel + '</strong>.';
        } else if (state.nextTier) {
          const remaining = state.nextTier.threshold - cartValue;
          message.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more to unlock <strong>' + state.nextTier.nextLabel + '</strong>.';
        }
      }

      // Only toggle the unlocked CLASS. Never touch the badge's SVG content.
      steps.forEach((step, index) => {
        const tier = CONFIG.tiers[index];
        if (!tier) return;
        step.classList.toggle('is-unlocked', cartValue >= tier.threshold);
      });
    });

    maybeCelebrate(state.currentIndex, cartValue);
  }

  function renderFromCart(cart) {
    if (!cart) return;
    renderRewards(getRewardSubtotal(cart), getPaidItemCount(cart));
    Upsell.sync(cart);
  }

  /* ---------- Optimistic (smooth) bar update ---------- */
  function getOptimisticCart(removeTarget) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    const rows = drawer.querySelectorAll('[data-meayurv-cart-line]');
    if (!rows.length) return null;
    let subtotal = 0, itemCount = 0;
    rows.forEach((row) => {
      if (row.dataset.meayurvIsGift === 'true') return;
      const unitPrice = Number(row.dataset.meayurvUnitPrice || 0);
      const input = row.querySelector('.quantity__input');
      let quantity = input ? Number(input.value || 0) : Number(row.dataset.meayurvQuantity || 0);
      if (removeTarget && row.contains(removeTarget)) quantity = 0;
      quantity = Math.max(0, quantity);
      subtotal += unitPrice * quantity;
      itemCount += quantity;
    });
    return { subtotal, itemCount };
  }
  function optimisticRefresh(event) {
    // A user action just changed (or is about to change) the cart. Bump the
    // action sequence so any in-flight reconcile that fetched an OLDER cart
    // state won't paint its now-stale value over this newer one (race fix).
    cartActionSeq++;
    const removeTarget = event && event.target.closest('cart-remove-button, .cart-remove-button');
    window.requestAnimationFrame(() => {
      const o = getOptimisticCart(removeTarget);
      if (o) renderRewards(o.subtotal, o.itemCount);
    });
  }

  /* ---------- Cart + gift operations ---------- */
  async function fetchCart() {
    const r = await fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Unable to fetch cart');
    return r.json();
  }
  function findGiftLines(cart, variantId) {
    return cart.items.map((item, index) => ({ item, line: index + 1 })).filter((e) => String(e.item.variant_id) === String(variantId));
  }
  async function addGifts(variantIds) {
    if (!variantIds.length) return null;
    const r = await fetch(getRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantIds.map((v) => ({ id: Number(v), quantity: 1, properties: { _meayurv_reward_gift: 'true' } })) })
    });
    if (!r.ok) { let b; try { b = await r.clone().json(); } catch (e) { b = await r.text(); } console.error('[MeAyurv] gift add failed:', { variantIds, status: r.status, b }); throw new Error('Unable to add reward gift'); }
    return r.json();
  }
  async function updateGiftLines(updates) {
    if (!updates || !Object.keys(updates).length) return null;
    const r = await fetch(getRoot() + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates })
    });
    if (!r.ok) throw new Error('Unable to update reward gift lines');
    return r.json();
  }
  async function reconcileGifts(cart) {
    let workingCart = cart || await fetchCart();
    let changed = false;
    const rewardSubtotal = getRewardSubtotal(workingCart);
    const updates = {};
    const giftsToAdd = [];
    CONFIG.gifts.forEach((gift) => {
      const giftLines = findGiftLines(workingCart, gift.variantId);
      const isEligible = rewardSubtotal >= gift.threshold;
      if (isEligible) {
        if (!giftLines.length) giftsToAdd.push(gift.variantId);
        else giftLines.forEach((gl, i) => {
          const key = gl.item.key || gl.line;
          if (i === 0) { if (Number(gl.item.quantity) !== 1) updates[key] = 1; } else updates[key] = 0;
        });
      } else {
        giftLines.forEach((gl) => { updates[gl.item.key || gl.line] = 0; });
      }
    });
    if (Object.keys(updates).length) { await updateGiftLines(updates); changed = true; workingCart = await fetchCart(); }
    if (giftsToAdd.length) { await addGifts(giftsToAdd); changed = true; }
    return changed;
  }
  async function renderDrawerAfterGiftChange() {
    const r = await fetch(getCartSectionUrl());
    if (!r.ok) return;
    const html = new DOMParser().parseFromString(await r.text(), 'text/html');
    ['cart-drawer-items', '.cart-drawer__footer'].forEach((sel) => {
      const target = document.querySelector(sel);
      const source = html.querySelector(sel);
      if (target && source) target.replaceWith(source);
    });
    lockGiftRows();
    CheckoutGate.reassert();
    Upsell.reinject();
    SavingsPanel.apply();
  }

  /* ---------- Gate controller (single state, full failsafe reset) ---------- */
  let reconcileRunning = false;
  let reconcilePendingAgain = false;
  let failsafeTimer = null;
  let idleTimer = null;
  // Monotonic counter bumped on every user cart action / cart-update. A
  // reconcile captures it before fetching and only paints the bar if it is
  // still the latest — prevents a slow, stale /cart.js response from briefly
  // reverting the bar to an older value (the new->old->new flicker).
  let cartActionSeq = 0;

  function armFailsafe() {
    window.clearTimeout(failsafeTimer);
    failsafeTimer = window.setTimeout(() => {
      console.warn('[MeAyurv] gate failsafe — forcing clean idle');
      reconcileRunning = false; reconcilePendingAgain = false;
      window.clearTimeout(idleTimer);
      CheckoutGate.set(false);
    }, GATE_FAILSAFE_MS);
  }
  function gateBusy() { window.clearTimeout(idleTimer); CheckoutGate.set(true); armFailsafe(); }
  function scheduleIdle() {
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => { if (!reconcileRunning) { window.clearTimeout(failsafeTimer); CheckoutGate.set(false); } }, GATE_IDLE_QUIET_MS);
  }

  async function runReconcile() {
    if (reconcileRunning) { reconcilePendingAgain = true; return; }
    reconcileRunning = true;
    gateBusy();
    try {
      do {
        reconcilePendingAgain = false;
        const seqAtStart = cartActionSeq;
        const cart = await fetchCart();
        const changed = await reconcileGifts(cart);
        if (changed) await renderDrawerAfterGiftChange();
        const finalCart = await fetchCart();
        // Only paint if no newer user action happened while we were fetching.
        // If one did, this fetched cart is stale; skip the paint and let the
        // loop re-run (reconcilePendingAgain) / the newer action's own render win.
        if (cartActionSeq === seqAtStart) renderFromCart(finalCart);
        lockGiftRows();
      } while (reconcilePendingAgain);
    } catch (e) {
      console.warn('[MeAyurv] reward reconcile failed:', e);
    } finally {
      reconcileRunning = false;
      scheduleIdle();
    }
  }

  function onCartUpdate(event) {
    cartActionSeq++;
    if (event && event.cartData) renderFromCart(event.cartData);
    SavingsPanel.apply(); // footer was rebuilt by Sense — re-open the panel if it was open
    runReconcile();
  }

  /* ---------- Gift row lock ---------- */
  function lockGiftRows() {
    document.querySelectorAll('[data-meayurv-is-gift="true"]').forEach((row) => {
      const input = row.querySelector('.quantity__input');
      const controls = row.querySelectorAll('.quantity__button, .cart-remove-button');
      if (input) { input.value = 1; input.setAttribute('readonly', 'readonly'); input.setAttribute('aria-readonly', 'true'); }
      controls.forEach((b) => { b.setAttribute('disabled', 'disabled'); b.setAttribute('aria-hidden', 'true'); });
    });
  }
  function isCartControl(target) {
    return Boolean(
      target.closest('button[name="plus"]') || target.closest('button[name="minus"]') ||
      target.closest('.quantity__input') || target.closest('cart-remove-button') || target.closest('.cart-remove-button')
    );
  }

  /* ---------- Bindings ---------- */
  function bindCartEvents() {
    document.addEventListener('click', (event) => {
      const giftRow = event.target.closest('[data-meayurv-is-gift="true"]');
      if (giftRow && isCartControl(event.target)) { event.preventDefault(); event.stopPropagation(); return; }
      if (!event.target.closest('cart-drawer')) return;

      // Intercept item removal: show a confirm sheet first, unless we're the ones
      // re-firing the click after the user confirmed. (Gifts are already blocked
      // by the gift-row guard above.)
      const removeBtn = event.target.closest('cart-remove-button, .cart-remove-button');
      if (removeBtn && !RemoveConfirm.bypassing()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        RemoveConfirm.open(removeBtn);
        return;
      }

      // Minus on a qty-1 item should REMOVE it (via the same confirm sheet), not
      // bottom out at Sense's "minimum quantity is 1". Intercept before Sense's
      // stepDown/validate runs.
      const minusBtn = event.target.closest('button[name="minus"]');
      if (minusBtn && !RemoveConfirm.bypassing()) {
        const row = minusBtn.closest('[data-meayurv-cart-line]') || minusBtn.closest('.cart-item');
        const input = row && row.querySelector('.quantity__input');
        const qty = input ? Number(input.value) : NaN;
        if (row && row.dataset.meayurvIsGift !== 'true' && qty <= 1) {
          const rowRemoveBtn = row.querySelector('.cart-remove-button');
          if (rowRemoveBtn) {
            event.preventDefault();
            event.stopImmediatePropagation();
            RemoveConfirm.open(rowRemoveBtn);
            return;
          }
        }
      }

      if (!isCartControl(event.target)) return;
      gateBusy();
      optimisticRefresh(event);
    }, true);

    ['input', 'change'].forEach((type) => {
      document.addEventListener(type, (event) => {
        if (!event.target.closest('cart-drawer')) return;
        if (!event.target.matches('.quantity__input')) return;
        if (event.target.closest('[data-meayurv-is-gift="true"]')) { event.target.value = 1; event.preventDefault(); return; }
        gateBusy();
        optimisticRefresh(event);
      }, true);
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('#cart-icon-bubble')) return;
      runReconcile();
    }, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    } else {
      console.warn('[MeAyurv] Sense pub/sub not found — reconcile relies on drawer-open + load only.');
    }
  }

  function init() {
    // Guard against binding twice. The document-level listeners are added once
    // per script execution; if this file is ever loaded/run a second time (an
    // accidental double-include, or a dev hot-reload), a second set of listeners
    // would fire every cart action twice and collide into a line-number desync
    // ("line parameter is invalid"). This flag makes a second run a no-op.
    if (window.__meayurvCartRewardsInit) return;
    window.__meayurvCartRewardsInit = true;
    lockGiftRows();
    bindCartEvents();
    Upsell.bind();
    Upsell.setupArrows();
    SavingsPanel.bind();
    runReconcile();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();